/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, DestroyRef, Input, OnInit, inject} from '@angular/core';

import template from './earnings-account-list-page.component.html';
import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserProfile} from '../../components/session/local-user-profile';
import {
  SignalAdminListColumn,
  SignalAdminResourceListState,
  SignalAdminResourceListStateFactory
} from '../common/controller/signal-admin-resource-list-state';
import {getCountryName} from '../common/directives/country-select/countries';
import {SiteApiService, SiteModel} from '../site/site-api.service';
import {EarningsAccountApiService, EarningsAccountModel} from './earnings-account-api.service';
import {AppSharedModule} from '../common/app-shared.module';
import {UIRouterModule} from '@uirouter/angular';
import {AdminListModule} from '../common/directives/admin-list.module';
import {AdminResourcePagesModule} from '../common/controller/admin-resource-pages.module';
import {ObjectNotesButtonComponent} from '../common/directives/notes/object-notes-button.component';

@Component({
  standalone: true,
  imports: [AppSharedModule, UIRouterModule, AdminListModule, AdminResourcePagesModule, ObjectNotesButtonComponent],
  selector: 'adn-earnings-account-list-page',
  template
})
export class EarningsAccountListPageComponent implements OnInit {
  constructor(private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()) {
  }

  readonly itemStateName = 'app.inv.earnings-accounts.earnings-account';
  readonly siteItemStateName = 'app.inv.sites.site';
  readonly showCountries = this.localNetworkInfo.getSiteCountries().length > 1;
  readonly showFullPage = inject(LocalNetworkProfile).isPureMarketplacePlatform()
    && ['adnuntius@adnuntius.com', 'broker1@adnuntius.com'].includes(inject(LocalUserProfile).get('username'));
  readonly columns: SignalAdminListColumn[] = [
    {param: 'name'},
    ...(this.showCountries ? [{label: 'countries', param: '', sortable: false}] : []),
    {param: 'externalReference'},
    {param: 'revenueShare'},
    ...(this.showFullPage ? [{label: 'fullPage', param: '', sortable: false}] : [])
  ];
  readonly childColumns: SignalAdminListColumn[] = [
    {param: 'name'}, {param: 'description'}, {param: 'siteUrl'}
  ];
  readonly list = inject(SignalAdminResourceListStateFactory).create<EarningsAccountModel>(
    inject(EarningsAccountApiService), {listId: 'fullEarningsAccountList'}, inject(DestroyRef)
  );
  private readonly factory = inject(SignalAdminResourceListStateFactory);
  private readonly siteApi = inject(SiteApiService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly children: Record<string, SignalAdminResourceListState<SiteModel>> = {};

  @Input() set readOnly(value: boolean) { this.list.readOnly.set(value); }

  ngOnInit(): void { this.list.load(); }

  countries(item: EarningsAccountModel): string {
    return (item.countries || []).map(getCountryName).join(', ');
  }

  toggleChildren(item: EarningsAccountModel): void {
    item.showChildren = !item.showChildren;
    if (!this.children[item.id]) {
      const childList = this.factory.create(this.siteApi, {
        listId: 'fullSiteList', baseTable: 'site', syncPageToUrl: false, additionalParams: {earningsAccount: item.id}
      }, this.destroyRef);
      childList.readOnly.set(true);
      this.children[item.id] = childList;
      childList.load();
    }
  }

  childList(id: string): SignalAdminResourceListState<SiteModel> | undefined { return this.children[id]; }
}
